import { Grid2, Typography, Container, Box, Popover, ToggleButtonGroup, ToggleButton } from "@mui/material";
import { memo, useState, useRef, useCallback, useEffect } from "react";
import { makeStyles } from "@mui/styles";
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import Form from "../components/Form.js";
import { useSnackbar } from "../utils/index.js";
import Spinner from "../components/Spinner.js";
import { inviteUser, getOrganizations, getKentros } from "../api/index.js";

const useStyles = makeStyles((theme) => ({
	form_title: {
		color: theme.palette.primary.main,
		display: "flex",
		alignItems: "center"
	},
}));

const InviteUser = () => {
	const classes = useStyles();

	const tabIndex = 0;
	const [invitationUrl, setInvitationUrl] = useState(null);
	const [isPopoverOpen, setIsPopoverOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(false);

	// User type: "regular" (belongs to an Organization) or "kentro" (belongs to a Kentro)
	const [userType, setUserType] = useState("regular");

	// Organizations
	const [fetchedOrganizations, setFetchedOrganizations] = useState([]);
	const [organizationsDropdownValues, setOrganizationsDropdownValues] = useState([]);

	// Kentros
	const [fetchedKentros, setFetchedKentros] = useState([]);
	const [kentrosDropdownValues, setKentrosDropdownValues] = useState([]);

	const formRef = useRef(null);

	const { error, success } = useSnackbar();

	const fetchData = useCallback(
		async () => {
			try {
				const [{ organizations }, { kentros }] = await Promise.all([
					getOrganizations(),
					getKentros(),
				]);

				setFetchedOrganizations(organizations);
				setOrganizationsDropdownValues(
					organizations.map((org) => ({ value: org.name, text: org.name }))
				);

				setFetchedKentros(kentros);
				setKentrosDropdownValues(
					kentros.map((k) => ({ value: String(k.kentro_id), text: k.name }))
				);
			} catch (err) {
				error(err);
			}
		}
	);

	useEffect(() => {
		fetchData();
	}, []);

	const regularUserFormContent = [
		{
			customType: "input",
			id: "email",
			type: "text",
			required: true,
			placeholder: "E-mail",
		},
		{
			customType: "input",
			id: "fullName",
			type: "text",
			required: true,
			placeholder: "Ονοματεπώνυμο",
		},
		{
			customType: "dropdown",
			id: "role",
			items: [
				{ value: "USER", text: "USER" },
				{ value: "ADMIN", text: "ADMIN" },
			],
			placeholder: "Ρόλος",
			required: true,
		},
		{
			customType: "dropdown",
			id: "organization",
			items: organizationsDropdownValues,
			placeholder: "Οργάνωση",
			required: true,
		},
		{
			customType: "button",
			id: "submit",
			type: "submit",
			text: "Υποβολή",
		}
	];

	const kentroUserFormContent = [
		{
			customType: "input",
			id: "email",
			type: "text",
			required: true,
			placeholder: "E-mail",
		},
		{
			customType: "input",
			id: "fullName",
			type: "text",
			required: true,
			placeholder: "Ονοματεπώνυμο",
		},
		{
			customType: "dropdown",
			id: "kentro",
			items: kentrosDropdownValues,
			placeholder: "Κέντρο",
			required: true,
		},
		{
			customType: "button",
			id: "submit",
			type: "submit",
			text: "Υποβολή",
		}
	];

	const inviteUserSubmitHandler = async (values) => {
		setIsLoading(true);
		try {
			let successCode, message, url;

			if (userType === "kentro") {
				const selectedKentro = fetchedKentros.find((k) => String(k.kentro_id) === values.kentro);
				const kentroId = selectedKentro?.kentro_id;
				({ success: successCode, message, url } = await inviteUser(
					values.email.trim(), "USER", null, values?.fullName, kentroId
				));
			} else {
				const organizationId = fetchedOrganizations.find((org) => org.name === values.organization)?.orgId;
				({ success: successCode, message, url } = await inviteUser(
					values.email.trim(), values.role, organizationId, values?.fullName
				));
			}

			if (successCode) {
				success(message);
				setIsPopoverOpen(true);
				setInvitationUrl(url);
			} else {
				error(message);
			}
		} catch { /* empty */ }

		setIsLoading(false);
	};

	const handleCopyToClipboard = () => {
		setIsLoading(true);
		if (invitationUrl) {
			navigator.clipboard.writeText(invitationUrl).then(() => {
				success("Ο σύνδεσμος αντιγράφηκε στο πρόχειρο.");
			}).catch(() => {
				error("Σφάλμα κατά την αντιγραφή συνδέσμου.");
			});
		}
		setIsLoading(false);
	};

	return (
		<>
			<Spinner open={isLoading} />
			<Grid2 container direction="column" alignItems="center" justifyContent="center">
				<Container maxWidth="xl" sx={{ paddingTop: 5 }}>
					<Box display="flex" justifyContent="center" alignItems="center" mt={5}>
						<Box display="flex" alignItems="center" justifyContent="center">
							<Box flex={1} p={3}>
								{tabIndex === 0 &&
									<Container maxWidth="xl" ref={formRef}>
										<Typography variant="h5" className={classes.form_title}>
											<PersonAddIcon fontSize='large' sx={{ marginRight: '8px' }} />
											Πρόσκληση Χρήστη
										</Typography>
										<Typography variant="subtitle1" className={classes.form_title}>
											Παρακαλώ συμπληρώστε την παρακάτω φόρμα για να προσκαλέσετε ένα νέο χρήστη:
										</Typography>

										{/* User type toggle */}
										<Box sx={{ mt: 2, mb: 1 }}>
											<Typography variant="body2" sx={{ mb: 1, color: "text.secondary" }}>
												Τύπος Χρήστη:
											</Typography>
											<ToggleButtonGroup
												exclusive
												value={userType}
												onChange={(_, val) => { if (val) setUserType(val); }}
												size="small"
											>
												<ToggleButton value="regular">Χρήστης Συνεταιρισμού</ToggleButton>
												<ToggleButton value="kentro">Χρήστης Κέντρου</ToggleButton>
											</ToggleButtonGroup>
										</Box>

										<Form
											content={userType === "kentro" ? kentroUserFormContent : regularUserFormContent}
											onSubmit={inviteUserSubmitHandler}
										/>

										<Popover
											open={isPopoverOpen}
											anchorEl={formRef.current}
											anchorOrigin={{
												vertical: 'bottom',
												horizontal: 'center',
											}}
											transformOrigin={{
												vertical: 'top',
												horizontal: 'center',
											}}
											PaperProps={{ elevation: 16 }}
											onClose={() => setIsPopoverOpen(false)}
										>
											<Box py={1} px={2}>
												<Typography style={{ fontWeight: 'bold' }}>{'Η πρόσκληση δημιουργήθηκε.'}</Typography>
												<Box mt={1}>
													<Typography variant="body2">{'Ο σύνδεσμος πρόσκλησης βρίσκεται παρακάτω:'}</Typography>
												</Box>
												<Box textAlign="center">
													<Typography
														variant="body2"
														color="primary"
														style={{ cursor: 'pointer' }}
														onClick={handleCopyToClipboard}
													>
														Αντιγραφή Συνδέσμου
													</Typography>
												</Box>
											</Box>
										</Popover>
									</Container>
								}
							</Box>
						</Box>
					</Box>
				</Container>
			</Grid2>
		</>
	);
};

export default memo(InviteUser);
